import { Stack, ListItem, Badge, Typography } from "@mui/material";
import { NavLink, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import type { ListNotificationsFragment } from "@/gql/graphql";
import {
  NotificationType,
  UserNotificationStatus,
  NotificationReason,
  ContractStatusType,
  ContractStatusReason,
  ListNotificationsFragmentDoc,
} from "@/gql/graphql";
import { useNotificationTargetUri } from "@/hooks/useNotificationTargetUri";
import { useRouteRefresh } from "@/hooks/useRouteRefresh";
import { isCurrentPath } from "@/utils/routeRefresh";
import { useRef } from "react";
import ContractStatusNotification from "./Data/ContractStatusNotification";
import CustomerRequestNotification from "./Data/CustomerRequestNotification";
import ContractRenewalNotification from "./Data/ContractRenewalNotification";
import { useNotificationsService } from "@/hooks/service/useNotificationsService";
import {
  PermissionAction,
  PermissionCategory,
  useSupportAdminWriteGuard,
} from "@/permissions";
import { usePermissionChecker } from "@/permissions/PermissionContext";
import TimeAgo from "./TimeAgo";
import ContractActivationReminderNotification from "./Data/ContractActivationReminderNotification";
import AmendmentRepriceNotification from "./Data/AmendmentRepriceNotification";
import ApprovalRequestNotification from "./Data/ApprovalRequestNotification";
import AutoRenewNotification from "./Data/AutoRenewNotification";
import BulkShipToContactNotification from "./Data/BulkShipToContactNotification";
import AddOnBaseProductMissingNotification from "./Data/AddOnBaseProductMissingNotification";
import { useFragment } from "@apollo/client/react";
import CustomerDeviceTransferNotification from "./Data/CustomerDeviceTransferNotification";
import DeviceStatusNotifications from "./Data/DeviceStatusNotification";
import DeviceTransferRequestApprovedNotification from "./Data/DeviceTransferRequestApprovedNotification";
import DeviceTransferRequestDeclinedNotification from "./Data/DeviceTransferRequestDeclinedNotification";
import DeviceTransferRequestExpiredNotification from "./Data/DeviceTransferRequestExpiredNotification";
import DeviceTransferRequestCancelledNotification from "./Data/DeviceTransferRequestCancelledNotification";
import DeviceTransferredToAnotherDealerNotification from "./Data/DeviceTransferredToAnotherDealerNotification";
import DataListItemText from "./Data/DataListItemText";

type NotificationProps = {
  notification: ListNotificationsFragment;
  onClick?: () => void;
};

const Notification = (props: NotificationProps) => {
  const { notification, onClick } = props;
  const { t } = useTranslation();
  const {
    id,
    data,
    notificationType,
    notificationStatus,
    contentUri,
    createdDateTime,
  } = notification;

  const { markAsRead } = useNotificationsService();
  const { guardWriteAction } = useSupportAdminWriteGuard();
  const { hasPermission, isSupportAdminWriteBlocked } = usePermissionChecker();
  const canManageNotifications = hasPermission(
    PermissionCategory.Notification,
    PermissionAction.CreateAndManage
  );
  const isSupportAdminViewOnly = isSupportAdminWriteBlocked(
    PermissionAction.CreateAndManage
  );
  const refreshCurrentRoute = useRouteRefresh();
  const location = useLocation();

  const targetUri = useNotificationTargetUri(contentUri);

  const handleMarkAsRead = () => {
    if (!canManageNotifications) {
      return;
    }
    guardWriteAction(PermissionAction.CreateAndManage, () => {
      void markAsRead(id);
    });
  };

  const hoverTimer = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (notificationStatus === UserNotificationStatus.Read) {
      return;
    }
    hoverTimer.current = setTimeout(handleMarkAsRead, 1000);
  };

  const handleMouseLeave = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
  };

  const handleClick = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    if (!isSupportAdminViewOnly) {
      handleMarkAsRead();
    }
    if (isCurrentPath(targetUri, location.pathname)) {
      refreshCurrentRoute();
    }
    if (onClick) onClick();
  };
  const { data: cachedNotification } = useFragment({
    fragment: ListNotificationsFragmentDoc,
    fragmentName: "ListNotifications",
    from: {
      __typename: "Notification",
      id: notification.id,
    },
  });
  const liveStatus =
    cachedNotification?.notificationStatus ?? notification.notificationStatus;
  const isUnread = liveStatus === UserNotificationStatus.Unread;
  return (
    <Stack
      sx={{ height: "100%", justifyContent: "center" }}
      data-testid="notification-item"
    >
      <ListItem
        component={NavLink}
        to={targetUri}
        dense
        disableGutters
        alignItems="flex-start"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        secondaryAction={
          <Badge
            variant="dot"
            color="primary"
            role="status"
            aria-label={t("aria.unread_notification")}
            data-testid="notification-badge"
            invisible={!isUnread}
          />
        }
      >
        {(() => {
          if (notificationType === NotificationType.PricelistGenerationFailed) {
            return (
              <DataListItemText
                primary={t("notifications.pricelist_generation_failed.title")}
                secondary={t(
                  "notifications.pricelist_generation_failed.description"
                )}
              />
            );
          }
          if (data?.__typename === "ContractStatusNotification") {
            if (
              data?.notificationReason ===
              NotificationReason.RenewalAddonMissingBase
            ) {
              return <AddOnBaseProductMissingNotification data={data} />;
            }
            if (notificationType === NotificationType.Renewal) {
              return <ContractRenewalNotification data={data} />;
            }
            if (
              data?.notificationReason ===
              NotificationReason.ContractActivationReminder
            ) {
              return <ContractActivationReminderNotification data={data} />;
            }
            if (
              data?.status === ContractStatusType.AmendmentOnHold &&
              data?.statusChangeReason === ContractStatusReason.PriceChange
            ) {
              return <AmendmentRepriceNotification data={data} />;
            }
            if (data?.status === ContractStatusType.Cancelled) {
              return <ContractStatusNotification data={data} />;
            }
            // Check if this is a customer request notification
            const customerRequestStatus = data?.customerRequestStatus;
            const isCustomerRequest =
              data?.__typename === "ContractStatusNotification" &&
              customerRequestStatus !== null &&
              customerRequestStatus !== undefined;
            if (isCustomerRequest) {
              return <CustomerRequestNotification data={data} />;
            }
            return <ContractStatusNotification data={data} />;
          }
          if (data?.__typename === "ContractApprovalStatusNotification") {
            return <ApprovalRequestNotification data={data} />;
          }
          if (data?.__typename === "AutoRenewUpdateNotification") {
            return <AutoRenewNotification data={data} />;
          }
          if (data?.__typename === "CustomerTransferDeviceNotification") {
            return <CustomerDeviceTransferNotification data={data} />;
          }
          if (data?.__typename === "DealerDeviceTransferNotification") {
            if (
              notificationType ===
              NotificationType.DeviceTransferRequestApproved
            ) {
              return <DeviceTransferRequestApprovedNotification data={data} />;
            }
            if (
              notificationType ===
              NotificationType.DeviceTransferRequestDeclined
            ) {
              return <DeviceTransferRequestDeclinedNotification data={data} />;
            }
            if (
              notificationType === NotificationType.DeviceTransferRequestExpired
            ) {
              return <DeviceTransferRequestExpiredNotification data={data} />;
            }
            if (
              notificationType ===
              NotificationType.DeviceTransferRequestCancelled
            ) {
              return <DeviceTransferRequestCancelledNotification data={data} />;
            }
            if (
              notificationType ===
              NotificationType.DeviceTransferredToAnotherDealer
            ) {
              return (
                <DeviceTransferredToAnotherDealerNotification data={data} />
              );
            }
          }
          if (data?.__typename === "DeviceStatusNotification") {
            if (notificationType === NotificationType.DeviceStatus) {
              return <DeviceStatusNotifications data={data} />;
            }
          }
          if (data?.__typename === "BulkStatusNotification") {
            return (
              <BulkShipToContactNotification
                data={data}
                contentUri={contentUri}
                onViewDetails={onClick}
              />
            );
          }
          return t("notifications.unhandled_type");
        })()}
      </ListItem>
      <Typography
        data-testid="notification-item-time"
        variant="caption"
        color="text.secondary"
      >
        <TimeAgo dateTime={createdDateTime} />
      </Typography>
    </Stack>
  );
};

export default Notification;
